import React from 'react';

import { useFeatureGate } from '../../hooks/useFeatureGate';
import type { FeatureFlagKey } from '../../config/featureFlags';

export type FeatureRouteProps = {
  /** The build-ahead feature this whole page belongs to. */
  flag: FeatureFlagKey;
  /** What renders when the section is not revealed — the app's own not-found. */
  fallback: React.ReactElement;
  children: React.ReactElement;
};

/**
 * A whole PAGE behind a feature flag: the section's own component while the flag
 * is on, the not-found page while it is off.
 *
 * Hiding a section's rail entry is not hiding the section. `novaRoutes` is built
 * once at module load and handed to the router, so a URL typed straight in — or
 * a stale bookmark, or a link from Lumio — still resolves to the page whatever
 * the rail is showing. A gate that only removes the menu item is decoration; the
 * route has to answer for itself, and this is where it does.
 *
 * Not-found rather than a redirect: a section that has not been revealed does
 * not exist as far as this owner is concerned, and bouncing them to Home instead
 * would tell them there IS something there they cannot reach. Reactive through
 * {@link useFeatureGate}, so revealing the flag from Lumio makes the page appear
 * under a URL already open, with no reload.
 */
export const FeatureRoute = ({ flag, fallback, children }: FeatureRouteProps): React.ReactElement => {
  const { isEnabled } = useFeatureGate();
  return isEnabled(flag) ? children : fallback;
};

export default FeatureRoute;
